import {
  PostHogAppContext,
  PostHogEventName as PostHogCustomEvent,
  EnumUserUserType,
  EnumUserAccessOperatorsRole,
} from "gql/generated";
import { usePostHog as usePostHogHook } from "posthog-js/react";
import { useAuth0 } from "@auth0/auth0-react";
import { useAppContext } from "useAppContext";
import { useLoggedInAs } from "./useLoggedInAs";
import { useCallback } from "react";
import { useAppVersionContext } from "useAppVersionContext";
import { WA_INTERNAL_EMAIL_DOMAINS } from "shared/constants/internalEmailDomains";
import { isInjectedWebKitBridgeException } from "shared/utils/isInjectedWebKitBridgeException";

// Export the enums for use in other files
// These two enums are defined in the back-end and come via the `gql/generated.ts` file
export { PostHogAppContext, PostHogCustomEvent };

export type PostHogCustomProperty =
  | "appContext"
  | "organizationId"
  | "operatorEntityId"
  | "widgetId"
  | "eventId"
  | "bookNowLink"
  | "selectedOperatorId"
  | "selectedFilterTagId"
  | "bookerId"
  | "productId"
  | "promoCode"
  | "orderId"
  | "instanceId"
  | "broadcastId"
  | "resourceId"
  | "personId"
  | "userId"
  | "loggedInAsType"
  | "loggedInAsEntityId"
  | "userCategory";

export type PostHogCustomProperties = Partial<
  Record<PostHogCustomProperty, string | number>
>;

export type PostHogExceptionDataProps = {
  fileName?: string;
  method?: string;
  message?: string;
  extra?: Record<string, string | number | boolean>;
};

export type UserCategory =
  | "wa_superadmin"
  | "wa_staff"
  | "org_admin"
  | "operator"
  | "user";

export function getUserCategory(user: {
  userType?: string | null;
  email?: string | null;
  access?: {
    organizations?: Array<{ role?: string | null } | null> | null;
    operators?: Array<unknown> | null;
  } | null;
}): UserCategory {
  if (user.userType === EnumUserUserType.TnbInternal) return "wa_superadmin";

  const email = (user.email ?? "").toLowerCase();
  if (WA_INTERNAL_EMAIL_DOMAINS.some(domain => email.endsWith(domain))) {
    return "wa_staff";
  }

  const orgs = user.access?.organizations ?? [];
  const hasOrgAdminRole = orgs.some(
    org =>
      org?.role === EnumUserAccessOperatorsRole.Owner ||
      org?.role === EnumUserAccessOperatorsRole.Editor,
  );
  if (hasOrgAdminRole) return "org_admin";

  if ((user.access?.operators ?? []).length > 0) return "operator";

  return "user";
}

let isInitialized = false;

/**
 * Use this hook to track PostHog events with type safety for event type
 * Must be used within a PostHogProvider context
 * @returns A function to track PostHog events with type safety for event type
 */
export const usePostHog = () => {
  const posthog = usePostHogHook();

  // Get the dbUser, auth0User, and loggedInAs values from their contexts
  // Since NOT all parts of the app will be wrapped in the respective contexts,
  // we need to use try/catch blocks to safely access the values
  const dbUser = useSaveDbUser();
  const auth0User = useSaveAuth0User();
  const loggedInAs = useSaveLoggedInAs();
  const currentVersion = useSaveAppVersion();

  /**
   * Initialize the PostHog instance exactly once per page. Multiple widgets
   * on the same page share the posthog-js singleton — re-init is a no-op in
   * posthog-js but we skip it early for clarity.
   */
  const initPostHog = (name: string, persistence_name?: string) => {
    if (isInitialized) return;
    isInitialized = true;

    if (["development", "production"].includes(import.meta.env.VITE_ENVIRONMENT ?? "")) {
      posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY, {
        api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
        name,
        persistence_name: persistence_name ?? name,
        defaults: "2025-11-30",
        disable_session_recording: true,
        opt_out_capturing_by_default: false,
        before_send: event =>
          isInjectedWebKitBridgeException(event) ? null : event,
      });
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).posthog = {
        init: () => {},
        capture: () => {},
        captureException: () => {},
        startSessionRecording: () => {},
        stopSessionRecording: () => {},
        register: () => {},
      };
    }
  };

  /**
   * Register a PostHog property with type safety for property type
   * @param posthog - The PostHog instance
   * @param properties - The properties to register
   */
  const registerPostHog = (properties: PostHogCustomProperties) => {
    posthog.register(properties);
  };

  /**
   * Track a PostHog event with type safety for event type
   * @param event - The event to track
   * @param properties - The properties to track with the event
   */
  const trackPostHogEvent = useCallback(
    (event: PostHogCustomEvent, properties?: PostHogCustomProperties) => {
      posthog.capture(event, properties);
    },
    [posthog],
  );

  /**
   * Get the PostHog error capture data
   * @param fileName - The file name of the error
   * @param method - The method of the error
   * @param message - The message of the error
   * @param extra - The extra properties to track with the error
   * @returns The additional PostHog error capture data
   */
  const getPostHogErrorCaptureData = useCallback(
    ({
      fileName,
      method,
      message,
      extra,
    }: PostHogExceptionDataProps): Record<
      string,
      string | number | boolean
    > => {
      const userId = dbUser?._id ?? "";
      const auth0Id = auth0User?.sub ?? "";
      const name =
        dbUser?.firstName && dbUser?.lastName
          ? `${dbUser.firstName} ${dbUser.lastName}`
          : (auth0User?.nickname ?? "");
      const email = dbUser?.email ?? auth0User?.email ?? "";

      return {
        ...(extra ? { ...extra } : {}),
        ...(fileName ? { fileName } : {}),
        ...(method ? { method } : {}),
        ...(message ? { message } : {}),
        ...(loggedInAs
          ? {
              loggedInAsType: loggedInAs.type,
              loggedInAsEntityId: loggedInAs.entityId,
            }
          : {}),
        ...(userId ? { userId } : {}),
        ...(auth0Id ? { auth0Id } : {}),
        ...(name ? { name } : {}),
        ...(email ? { email } : {}),
        ...(currentVersion ? { whereaboutsVersion: currentVersion } : {}),
      };
    },
    [dbUser, auth0User, loggedInAs, currentVersion],
  );

  /**
   * Tracks a PostHost exception
   * - Starts a session recording (post hog internally caches recording, so we'll get a couple of seconds before the exception)
   * - Stops session recording after 10 seconds (to avoid excessive data collection)
   * @param error - The error to track
   * @param properties - The extra properties to track with the error
   */
  const trackPostHogException = useCallback(
    (
      error: unknown,
      { fileName, method, message, extra }: PostHogExceptionDataProps,
    ) => {
      console.log("trackPostHogException", posthog, error, {
        fileName,
        method,
        message,
        extra,
      });
      posthog.startSessionRecording(true);
      posthog.captureException(
        error,
        getPostHogErrorCaptureData({ fileName, method, message, extra }),
      );
      setTimeout(() => posthog.stopSessionRecording(), 10000);
    },
    [posthog, getPostHogErrorCaptureData],
  );

  const identifyPostHog = useCallback(
    (user: {
      _id: string;
      email?: string | null;
      firstName?: string | null;
      lastName?: string | null;
      userType?: string | null;
      access?: {
        organizations?: Array<{ role?: string | null } | null> | null;
        operators?: Array<unknown> | null;
        storefronts?: Array<unknown> | null;
      } | null;
    }) => {
      const userCategory = getUserCategory(user);

      posthog.identify(user._id, {
        email: user.email,
        name: `${user.firstName} ${user.lastName}`,
        userType: user.userType,
        userCategory,
        orgCount: user.access?.organizations?.length ?? 0,
        operatorCount: user.access?.operators?.length ?? 0,
        storefrontCount: user.access?.storefronts?.length ?? 0,
      });
    },
    [posthog],
  );

  const groupPostHog = useCallback(
    (group: { type: string; entityId: string }) => {
      if (group.type === "ORGANIZATION") {
        posthog.group("organization", group.entityId);
      }
    },
    [posthog],
  );

  return {
    posthog,
    initPostHog,
    registerPostHog,
    trackPostHogEvent,
    trackPostHogException,
    getPostHogErrorCaptureData,
    identifyPostHog,
    groupPostHog,
  };
};

const useSaveDbUser = () => {
  try {
    return useAppContext().dbUser;
  } catch {
    return undefined;
  }
};

const useSaveAuth0User = () => {
  try {
    return useAuth0().user;
  } catch {
    return undefined;
  }
};

const useSaveLoggedInAs = () => {
  try {
    return useLoggedInAs();
  } catch {
    return undefined;
  }
};

const useSaveAppVersion = () => {
  try {
    return useAppVersionContext().currentVersion;
  } catch {
    return undefined;
  }
};
